import { useCallback, useEffect, useState, useRef } from "react"
import { toast } from "react-toastify"
import {
  DiscountData,
  getTransportRemainingMonths,
} from "../../../service/discount"
import { createTransportInvoice } from "../../../service/fee-invoices"
import { useReactToPrint } from "react-to-print"
import InvoicePrintModal from "../../peoples/students/student-details/invoicePrintModal"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { selectUser } from "../../../core/data/redux/slice/user"

const TRANSPORT_FEE_GROUP_NAME = "Transport"

let _pending: { p: string; promise: Promise<any> } | null = null

export type TransportStudentDisplay = {
  firstName: string
  lastName: string
  grNumber: string
}

type Props = {
  studentId: string
  sessionId: string
  studentDisplay?: TransportStudentDisplay
  branchId?: string
  onClose: () => void
  openImmediately?: boolean
}

type MonthlyFeeItem = {
  id: string
  month: string
  fees: number
  discount: {
    profile: number
    manual: number
  }
  fine: number
  amount: number
  note: string
}

type OtherFeeItem = {
  id: string
  name: string
  amount: number
  discount: {
    profile: number
    manual: number
  }
  note: string
  feeGroupId?: string
  feeGroupName?: string
  feeTypeId?: string
  month?: string
  allocationId?: string
  invoiceId?: string
}

type OtherFeeWithMonths = {
  fee: any
  remainingMonths: string[]
}

const TransportCollectFeesModal = ({
  studentId,
  sessionId,
  studentDisplay,
  branchId,
  onClose,
  openImmediately = false,
}: Props) => {
  const user = useSelector(selectUser)
  const [loading, setLoading] = useState(true)
  const [createFeeLoading, setCreateFeeLoading] = useState(false)
  const [monthlyFeesData, setMonthlyFeesData] = useState<MonthlyFeeItem[]>([])
  const [otherFeesData, setOtherFeesData] = useState<OtherFeeItem[]>([])
  const [remainingMonths, setRemainingMonths] = useState<string[]>([])
  const [allAvailableMonths, setAllAvailableMonths] = useState<string[]>([])
  const [feeType, setFeeType] = useState<any>(null)
  const [discountData, setDiscountData] = useState<DiscountData>()
  const [otherFees, setOtherFees] = useState<any[]>([])
  const [otherFeesWithMonthsList, setOtherFeesWithMonthsList] = useState<
    OtherFeeWithMonths[]
  >([])
  const [availableTransportMonths, setAvailableTransportMonths] = useState<OtherFeeItem[]>([])
  const [studentFromTransport, setStudentFromTransport] = useState<{
    name?: string
    grNumber?: string
    grade?: string
    section?: string
  } | null>(null)
  const [generatedInvoice, setGeneratedInvoice] = useState<any>(null)
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [feeErrorMessage, setFeeErrorMessage] = useState<string | null>(null)
  const [withPrint, setWithPrint] = useState<boolean | null>(false)

  const printRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Fee-Receipt-${generatedInvoice?.invoiceNumber || ""}`,
    onAfterPrint: () => {
      setShowPrintModal(false)
      setGeneratedInvoice(null)
    },
  })

  const handleViewInvoices = () => {
    handleCloseModal()
    if (studentId) {
      navigate(`/student/student-details/${studentId}/fees`)
    }
  }

  const calculateMaxAllowedDiscount = (baseFee: number): number => {
    if (!discountData) return baseFee

    const profileDiscountAmount =
      discountData.discountType === "percentage"
        ? baseFee * (discountData.discount / 100)
        : discountData.discount

    return Math.max(0, baseFee - profileDiscountAmount)
  }

  const calculateProfileDiscount = (baseFee: number): number => {
    if (!discountData) {
      return 0
    }

    const profileDiscountAmount =
      discountData.discountType === "percentage"
        ? baseFee * (discountData.discount / 100)
        : discountData.discount

    return profileDiscountAmount
  }

  const formatIsoOrYyyyMmToShort = (dateStr: string): string => {
    if (dateStr.match(/^\d{4}-\d{2}$/)) {
      return formatMonthToShort(dateStr)
    }
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr
    const year = d.getFullYear()
    const monthNum = d.getMonth()
    const shortMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    return `${shortMonths[monthNum]}(${year})`
  }

  const formatMonthToShort = (monthString: string): string => {
    if (monthString.match(/^\d{4}-\d{2}$/)) {
      const [year, month] = monthString.split("-")
      const monthMap: Record<string, string> = {
        "01": "Jan",
        "02": "Feb",
        "03": "Mar",
        "04": "Apr",
        "05": "May",
        "06": "Jun",
        "07": "Jul",
        "08": "Aug",
        "09": "Sep",
        "10": "Oct",
        "11": "Nov",
        "12": "Dec",
      }
      return `${monthMap[month]}(${year})`
    }

    const match = monthString.match(/([A-Za-z]+)\((\d{4})\)/)
    if (!match) return monthString

    const monthName = match[1]
    const year = match[2]

    const monthMap: Record<string, string> = {
      January: "Jan",
      February: "Feb",
      March: "Mar",
      April: "Apr",
      May: "May",
      June: "Jun",
      July: "Jul",
      August: "Aug",
      September: "Sep",
      October: "Oct",
      November: "Nov",
      December: "Dec",
    }

    const shortMonth = monthMap[monthName] || monthName.substring(0, 3)
    return `${shortMonth}(${year})`
  }

  const formatMonthToBackend = (monthString: string): string => {
    const match = monthString.match(/([A-Za-z]+)\((\d{4})\)/)
    if (!match) return monthString

    const shortMonth = match[1]
    const year = match[2]

    const monthMap: Record<string, string> = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    }

    const monthNumber = monthMap[shortMonth] || "01"
    return `${year}-${monthNumber}`
  }

  const sortMonthsChronologically = (months: string[]): string[] =>
    [...months].sort((a, b) =>
      formatMonthToBackend(a).localeCompare(formatMonthToBackend(b))
    )

  const monthMap: Record<string, number> = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  }

  // Transport has no late fee, by explicit request -- this used to run its
  // own copy of the tuition late-fee ladder (first an uncapped
  // floor((daysPassed-1)/10)*100 formula, then briefly aligned to the same
  // capped 100/200/400 schedule as Collect Fees), but transport was never
  // supposed to charge one at all. Kept as a function (not deleted) so the
  // one call site below doesn't need touching -- it always returns 0 now.
  const calculateFine = (_monthString: string): number => {
    return 0
  }

  const handleMonthSelect = (month: string) => {
    if (monthlyFeesData.some(item => item.month === month)) {
      toast.warning(`Month ${month} is already added`)
      return
    }

    const tuitionFeeAmount = feeType?.amount || 0
    const profileDiscountAmount = calculateProfileDiscount(tuitionFeeAmount)
    const fineAmount = calculateFine(month)

    const newFeeItem: MonthlyFeeItem = {
      id: Math.random().toString(36).substr(2, 9),
      month,
      fees: tuitionFeeAmount,
      discount: {
        profile: profileDiscountAmount,
        manual: 0,
      },
      fine: fineAmount,
      amount: tuitionFeeAmount - profileDiscountAmount + fineAmount,
      note: fineAmount > 0 ? `Fine: ${fineAmount}` : "",
    }

    setMonthlyFeesData(prev => [...prev, newFeeItem])
    setRemainingMonths(prev => prev.filter(m => m !== month))
  }

  const handleOtherFeeSelect = (feeItem: OtherFeeItem) => {
    if (otherFeesData.some(item => item.id === feeItem.id)) {
      toast.warning(`Fee ${feeItem.name} is already added`)
      return
    }

    const newOtherFeeItem: OtherFeeItem = {
      ...feeItem,
      discount: {
        profile: 0,
        manual: 0,
      },
    }

    setOtherFeesData(prev => [...prev, newOtherFeeItem])
  }

  const handleOtherFeeMonthSelect = (feeWithMonths: OtherFeeWithMonths, month: string) => {
    const { fee } = feeWithMonths
    const rowId = `other-${fee._id}-${month}`
    if (otherFeesData.some(item => item.id === rowId)) {
      toast.warning(`${fee.name} for ${month} is already added`)
      return
    }

    const newOtherFeeItem: OtherFeeItem = {
      id: rowId,
      name: fee.name,
      amount: fee.amount,
      discount: { profile: 0, manual: 0 },
      note: `${fee.feeGroupName || fee.name} - ${month}`,
      feeGroupId: fee.feeGroup,
      feeGroupName: fee.feeGroupName,
      feeTypeId: fee._id,
      month,
      allocationId: fee.allocationId,
    }

    setOtherFeesData(prev => [...prev, newOtherFeeItem])
    setOtherFeesWithMonthsList(prev =>
      prev.map(item =>
        item.fee._id === fee._id
          ? { ...item, remainingMonths: item.remainingMonths.filter(m => m !== month) }
          : item
      )
    )
  }

  const handleFeeChange = (index: number, field: string, value: string) => {
    const updatedFees = [...monthlyFeesData]
    const numValue = Number(value) || 0

    if (field === "discount.manual") {
      const maxAllowedDiscount = calculateMaxAllowedDiscount(
        updatedFees[index].fees
      )

      if (numValue > maxAllowedDiscount) {
        return
      }

      updatedFees[index] = {
        ...updatedFees[index],
        discount: {
          ...updatedFees[index].discount,
          manual: numValue,
        },
      }
    } else {
      updatedFees[index] = {
        ...updatedFees[index],
        [field]: field === "note" ? value : numValue,
      }
    }

    const profileDiscountAmount = calculateProfileDiscount(
      updatedFees[index].fees
    )
    updatedFees[index].amount =
      updatedFees[index].fees -
      updatedFees[index].discount.manual -
      profileDiscountAmount +
      updatedFees[index].fine

    setMonthlyFeesData(updatedFees)
  }

  const handleOtherFeeChange = (
    index: number,
    field: string,
    value: string
  ) => {
    const updatedFees = [...otherFeesData]
    const numValue = Number(value) || 0

    if (field === "discount.manual") {
      const maxDiscount = updatedFees[index].amount
      if (numValue > maxDiscount) return
      updatedFees[index] = {
        ...updatedFees[index],
        discount: {
          ...updatedFees[index].discount,
          manual: numValue,
        },
      }
    } else if (field === "amount") {
      updatedFees[index] = {
        ...updatedFees[index],
        amount: numValue,
      }
    } else {
      updatedFees[index] = {
        ...updatedFees[index],
        [field]: field === "note" ? value : numValue,
      }
    }

    setOtherFeesData(updatedFees)
  }

  const removeFeeRow = (index: number) => {
    const removedFeeItem = monthlyFeesData[index]
    const removedMonth = removedFeeItem.month

    setMonthlyFeesData(prev => prev.filter((_, i) => i !== index))
    setRemainingMonths(prev => {
      const updatedMonths = [...prev, removedMonth]

      return updatedMonths.sort((a, b) => {
        const getMonthValue = (monthStr: string) => {
          const match = monthStr.match(/([A-Za-z]+)\((\d{4})\)/)
          if (!match) return 0
          const monthName = match[1] as keyof typeof monthMap
          const year = parseInt(match[2], 10)
          return year * 12 + monthMap[monthName]
        }
        return getMonthValue(a) - getMonthValue(b)
      })
    })
  }

  const removeOtherFeeRow = (index: number) => {
    const removed = otherFeesData[index]
    if (!removed) return
    setOtherFeesData(prev => prev.filter((_, i) => i !== index))
    setAvailableTransportMonths(prev => [...prev, removed])
    if (removed?.month && removed?.feeTypeId) {
      setOtherFeesWithMonthsList(prev =>
        prev.map(item =>
          item.fee._id === removed.feeTypeId
            ? {
                ...item,
                remainingMonths: sortMonthsChronologically([
                  ...item.remainingMonths,
                  removed.month!,
                ]),
              }
            : item
        )
      )
    }
  }

  const addTransportMonthBack = (item: OtherFeeItem) => {
    setAvailableTransportMonths(prev => prev.filter(x => x.id !== item.id))
    setOtherFeesData(prev => [...prev, item])
  }

  const calculateGrandTotal = () => {
    const monthlySubtotal = monthlyFeesData.reduce((sum, item) => {
      const profileDiscountAmount = calculateProfileDiscount(item.fees)
      return (
        sum +
        (item.fees - item.discount.manual - profileDiscountAmount + item.fine)
      )
    }, 0)

    const otherFeesSubtotal = otherFeesData.reduce((sum, item) => {
      return sum + (item.amount - item.discount.manual)
    }, 0)

    return monthlySubtotal + otherFeesSubtotal
  }

  const calculateFinalAmount = (record: any) => {
    const subTotal = record.subTotal || 0
    const fine = record.fine || 0
    const totalDiscount =
      (record.discount?.profile || 0) + (record.discount?.manual || 0)
    return subTotal - totalDiscount + fine
  }

  const handleGenerateInvoice = async (_isPrint?: boolean) => {
    setCreateFeeLoading(true)
    try {
      const transportInvoices = otherFeesData.map(item => ({
        month: item.month ? formatMonthToBackend(item.month) : "",
        discount: {
          profile: item.discount?.profile ?? 0,
          manual: item.discount?.manual ?? 0,
        },
        fine: 0,
        status: "paid",
      }))

      const response = await createTransportInvoice({
        studentId,
        transportInvoices,
      })

      if (response.success) {
        toast.success(response.message || "Transport fees created and paid successfully!")

        const createdInvoices = Array.isArray(response.data) ? response.data : []
        const firstInv = createdInvoices[0]

        const totalDiscountManual = otherFeesData.reduce(
          (s, i) => s + (i.discount?.manual ?? 0),
          0
        )
        const totalDiscountProfile = otherFeesData.reduce(
          (s, i) => s + (i.discount?.profile ?? 0),
          0
        )
        const grandTotal = calculateGrandTotal()

        const slipInvoice = {
          invoiceNumber:
            firstInv?.invoiceNumber ?? `Transport-${Date.now()}`,
          createdAt: firstInv?.createdAt ?? new Date().toISOString(),
          invoiceItems: otherFeesData.map(item => ({
            month: item.month ? formatMonthToBackend(item.month) : "",
            feeGroup: { name: TRANSPORT_FEE_GROUP_NAME },
            amount: item.amount,
            description: `Transport Fee - ${item.month || ""}`,
          })),
          discount: {
            profile: totalDiscountProfile,
            manual: totalDiscountManual,
          },
          fine: 0,
          subTotal: otherFeesData.reduce((s, i) => s + i.amount, 0),
          total: grandTotal,
          createdBy: null,
        }

        setGeneratedInvoice(slipInvoice)
        setShowPrintModal(true)
      } else {
        toast.error(response.message || "Failed to create transport invoice")
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create transport invoice"
      toast.error(msg)
    } finally {
      setCreateFeeLoading(false)
    }
  }

  useEffect(() => {
    if (!studentId || !sessionId) return

    const p = `${studentId}|${sessionId}|${branchId ?? ""}`
    let promise: Promise<any>
    if (_pending?.p === p) {
      promise = _pending.promise
    } else {
      promise = getTransportRemainingMonths(studentId, sessionId)
      _pending = { p, promise }
    }

    let cancelled = false
    setLoading(true)
    setFeeErrorMessage(null)
    setMonthlyFeesData([])
    setOtherFeesData([])
    setRemainingMonths([])
    setAllAvailableMonths([])
    setOtherFees([])
    setOtherFeesWithMonthsList([])

    promise
      .then((transportRes: any) => {
        if (cancelled) return

        if (!transportRes.success || !transportRes.data) {
          setFeeErrorMessage(
            transportRes.data?.message ||
              (transportRes as any).message ||
              "Failed to load transport remaining months"
          )
          return
        }

        const remainingMonthsFromApi = transportRes.data.remainingMonths || []
        const transportFeeItems: OtherFeeItem[] = remainingMonthsFromApi.map(
          (item: { month: string; amount: number; allocationId: string }) => {
            const monthShort = formatMonthToShort(item.month)
            return {
              id: `transport-${item.month}-${item.allocationId}`,
              name: "Transport",
              amount: item.amount,
              discount: { profile: 0, manual: 0 },
              note: `Transport - ${monthShort}`,
              feeGroupName: TRANSPORT_FEE_GROUP_NAME,
              month: monthShort,
              allocationId: item.allocationId,
            }
          }
        )
        setOtherFeesData([])
        setAvailableTransportMonths(transportFeeItems)
        setDiscountData(undefined)
        const stu = transportRes.data?.student
        const currentEnrollment = transportRes.data?.currentEnrollment
        const userId = stu?.userId
        const nameFromUserId =
          userId && (userId.firstName || userId.lastName)
            ? [userId.firstName, userId.lastName].filter(Boolean).join(" ").trim()
            : ""
        const displayName =
          (stu?.name && stu.name.trim()) || nameFromUserId || undefined
        const grNumber = stu?.grNumber
        const grade =
          currentEnrollment?.class?.name ?? stu?.class?.name ?? undefined
        const section =
          currentEnrollment?.section?.name ?? stu?.section?.name ?? undefined
        const hasStudent =
          (stu || currentEnrollment) &&
          (displayName || grNumber || grade || section)
        setStudentFromTransport(
          hasStudent
            ? {
                name: displayName,
                grNumber,
                grade,
                section,
              }
            : null
        )
      })
      .catch(err => {
        if (!cancelled) {
          console.error("Error loading transport fee data:", err)
          setFeeErrorMessage(
            err?.response?.data?.message || err?.message || "Failed to load transport fee data"
          )
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [studentId, sessionId, branchId])

  useEffect(() => {
    if (allAvailableMonths.length > 0) {
      const availableMonths = allAvailableMonths.filter(
        month => !monthlyFeesData.some(item => item.month === month)
      )
      setRemainingMonths(availableMonths)
    }
  }, [monthlyFeesData, allAvailableMonths])

  const handleCloseModal = () => {
    const el = document.getElementById("transport_create_fee_modal")
    if (el) {
      el.setAttribute("data-bs-dismiss", "modal")
      el.click()
      el.removeAttribute("data-bs-dismiss")
    }
    setMonthlyFeesData([])
    setOtherFeesData([])
    setRemainingMonths([])
    setAllAvailableMonths([])
    setOtherFees([])
    setOtherFeesWithMonthsList([])
    setAvailableTransportMonths([])
    setStudentFromTransport(null)
    setFeeType(null)
    setDiscountData(undefined)
    setFeeErrorMessage(null)
    _pending = null
    onClose()
  }

  const handleCloseButtonClick = () => {
    _pending = null
    onClose()
    setMonthlyFeesData([])
    setOtherFeesData([])
    setRemainingMonths([])
    setAllAvailableMonths([])
    setOtherFees([])
    setOtherFeesWithMonthsList([])
    setAvailableTransportMonths([])
    setStudentFromTransport(null)
    setFeeType(null)
    setDiscountData(undefined)
    setFeeErrorMessage(null)
  }

  const handlePrintModalClose = () => {
    setShowPrintModal(false)
    setGeneratedInvoice(null)   
    handleCloseModal()
  }

  const feeIdsWithMonths = new Set(
    otherFeesWithMonthsList.map(item => item.fee._id)
  )
  
  const availableOtherFees = otherFees
    .filter(
      (fee: any) =>
        !feeIdsWithMonths.has(fee._id) &&
        !otherFeesData.some(selectedFee => selectedFee.feeTypeId === fee._id)
    )
    .map((fee: any) => ({
      id: `other-${fee._id}`,
      name: fee.name,
      amount: fee.amount,
      discount: {
        profile: 0,
        manual: 0,
      },
      note: `${fee.feeGroupName || fee.name} Fee${
        fee.applicableFrom
          ? ` - ${new Date(fee.applicableFrom).toLocaleDateString()}`
          : ""
      }`,
      feeGroupId: fee.feeGroup,
      feeGroupName: fee.feeGroupName,
      feeTypeId: fee._id,
    }))

  return (
    <>
      {openImmediately && (
        <div
          className="modal-backdrop fade show"
          style={{ zIndex: 1040 }}
          aria-hidden="true"
          onClick={onClose}
        />
      )}
      <div
        className={`modal fade${openImmediately ? " show" : ""}`}
        id="transport_create_fee_modal"
        style={openImmediately ? { display: "block", zIndex: 1050 } : undefined}
        tabIndex={-1}
        aria-modal="true"
        aria-hidden={!openImmediately}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                TRANSPORT FEE COLLECTION
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseButtonClick}
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <form>
              <div
                className="modal-body"
                style={{ maxHeight: "70vh", overflowY: "auto" }}
              >
                {loading && !feeErrorMessage ? (
                  <div
                    className="d-flex flex-column justify-content-center align-items-center py-5"
                  >
                    <span className="spinner-border text-primary mb-2" />
                    <span className="text-muted">Loading fees, please wait...</span>
                  </div>
                ) : (
                  <>
                {(studentDisplay || studentFromTransport) && (
                  <div className="bg-light p-3 pb-0 rounded mb-4">
                    <div className="row align-items-center">
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <span className="fs-12 mb-1 text-muted">Name</span>
                          <p className="text-dark mb-0">
                            {studentDisplay?.firstName || studentDisplay?.lastName
                              ? `${studentDisplay.firstName ?? ""} ${studentDisplay.lastName ?? ""}`.trim()
                              : studentFromTransport?.name ?? "–"}
                          </p>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <span className="fs-12 mb-1 text-muted">
                            GR Number
                          </span>
                          <p className="text-dark mb-0">
                            {studentDisplay?.grNumber ?? studentFromTransport?.grNumber ?? "–"}
                          </p>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <span className="fs-12 mb-1 text-muted">Grade</span>
                          <p className="text-dark mb-0">
                            {studentFromTransport?.grade ?? "–"}
                          </p>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <span className="fs-12 mb-1 text-muted">Section</span>
                          <p className="text-dark mb-0">
                            {studentFromTransport?.section ?? "–"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                  </>
                )}

                {/* SELECT MONTHS - only months not in table; row months not shown here; on cancel/reopen all show here again */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="text-muted mb-0">
                        SELECT MONTHS (Available: {availableTransportMonths.length})
                      </h6>
                      {/* <div className="d-flex gap-2">
                        <button
                          type="button"
                          className={`btn btn-primary btn-sm ${
                            user.permissions?.includes("inoices.view") ||
                            !user.permissions?.length
                              ? ""
                              : "not-allowed"
                          }`}
                          onClick={e => {
                            if (
                              !user.permissions?.includes("fees.view") &&
                              !!user.permissions?.length
                            ) {
                              e.preventDefault()
                            } else {
                              handleViewInvoices()
                            }
                          }}
                        >
                          View Invoices
                          {!user.permissions?.includes("fees.view") &&
                            !!user.permissions?.length && (
                              <small className="ms-1">(No permission)</small>
                            )}
                        </button>
                      </div> */}
                    </div>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {availableTransportMonths.map(item => (
                        <button
                          key={item.id}
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => addTransportMonthBack(item)}
                          title={`Add ${item.month || item.note?.replace("Transport - ", "")} to table`}
                        >
                          {item.month || item.note?.replace("Transport - ", "")}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-12">
                    {/* Available Transport Fees Section */}
                    {availableOtherFees.length > 0 && (
                      <div className="mb-3">
                        <h6 className="text-muted mb-4">
                          SELECT TRANSPORT FEES (Available:{" "}
                          {availableOtherFees.length})
                        </h6>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {availableOtherFees.map(feeItem => (
                            <button
                              key={feeItem.id}
                              type="button"
                              className="btn btn-sm btn-outline-primary mb-4"
                              onClick={() => handleOtherFeeSelect(feeItem)}
                              title={`Click to add ${feeItem.name} to fees table`}
                            >
                              {feeItem.name} - {feeItem.amount}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="text-primary mb-0">TRANSPORT FEES</h5>
                    </div>
                    <div
                      className="table-responsive"
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <table className="table table-bordered">
                        <thead
                          className="table-light"
                          style={{ position: "sticky", top: 0, zIndex: 1 }}
                        >
                          <tr>
                            <th>FEE NAME</th>
                            <th>MONTH</th>
                            <th>TYPE</th>
                            <th>FEES</th>
                            <th>MANUAL DISCOUNT</th>
                            <th>AMOUNT</th>
                            <th>NOTE</th>
                            <th>ACTION</th>
                          </tr>
                        </thead>
                        <tbody>
                          {otherFeesData.map((feeItem, index) => {
                            const totalAmount =
                              feeItem.amount - feeItem.discount.manual

                            const canEdit =
                              user.permissions?.includes("fees.edit") ||
                              !user.permissions?.length

                            return (
                              <tr key={feeItem.id}>
                                {/* FEE NAME */}
                                <td>
                                  <strong>{feeItem.name}</strong>
                                </td>

                                {/* MONTH */}
                                <td>
                                  {feeItem.month ? (
                                    <strong>{feeItem.month}</strong>
                                  ) : (
                                    <span className="text-muted">–</span>
                                  )}
                                </td>

                                {/* TYPE */}
                                <td>
                                  <span className="badge bg-info">
                                    {feeItem.feeGroupName || "Other"}
                                  </span>
                                </td>

                                {/* FEES */}
                                <td>
                                  <input
                                    type="number"
                                    className="form-control bg-light"
                                    value={feeItem.amount || ""}
                                    readOnly
                                    title="Fees amount from API"
                                  />
                                </td>

                                {/* MANUAL DISCOUNT */}
                                <td>
                                  <input
                                    type="number"
                                    className={`form-control ${
                                      canEdit ? "" : "bg-light not-allowed"
                                    }`}
                                    value={feeItem.discount.manual || ""}
                                    readOnly={!canEdit}
                                    onChange={e =>
                                      handleOtherFeeChange(
                                        index,
                                        "discount.manual",
                                        e.target.value
                                      )
                                    }
                                    max={feeItem.amount}
                                    title={`Maximum discount allowed: ${feeItem.amount}`}
                                  />
                                </td>

                                {/* AMOUNT */}
                                <td>
                                  <input
                                    type="number"
                                    className="form-control bg-light"
                                    value={totalAmount}
                                    readOnly
                                  />
                                </td>

                                {/* NOTE */}
                                <td>
                                  <input
                                    type="text"
                                    className={`form-control ${
                                      canEdit ? "" : "bg-light not-allowed"
                                    }`}
                                    readOnly={!canEdit}
                                    value={feeItem.note}
                                    onChange={e =>
                                      handleOtherFeeChange(
                                        index,
                                        "note",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>

                                {/* ACTION */}
                                <td>
                                  <button
                                    type="button"
                                    className={`btn btn-danger btn-sm ${
                                      canEdit ? "" : "not-allowed"
                                    }`}
                                    onClick={e => {
                                      if (canEdit) {
                                        removeOtherFeeRow(index)
                                      } else {
                                        e.preventDefault()
                                      }
                                    }}
                                  >
                                    <i className="ti ti-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            )
                          })}

                          {otherFeesData.length === 0 && (
                            <tr>
                              <td
                                colSpan={8}
                                className="text-center text-muted py-4"
                              >
                                No transport months due. Remaining months are loaded from transport allocation.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <div className="p-3 rounded text-end bg-light">
                      <h4 className="mb-0 text-primary">
                        GRAND TOTAL: {calculateGrandTotal().toLocaleString()}
                      </h4>
                      <p className="text-muted mb-0">
                        {`Includes ${otherFeesData.length} transport fee(s)`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer pt-3">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={onClose}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className={`btn btn-primary ${
                    user.permissions?.includes("fees.collect") ||
                    !user.permissions?.length
                      ? ""
                      : "not-allowed"
                  }`}
                  onClick={() => handleGenerateInvoice(true)}
                  disabled={
                    (monthlyFeesData.length === 0 &&
                      otherFeesData.length === 0) ||
                    createFeeLoading ||
                    !studentId ||
                    !(
                      user.permissions?.includes("fees.collect") ||
                      !user.permissions?.length
                    )
                  }
                >
                  {withPrint && createFeeLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Processing...
                    </>
                  ) : (
                    "Generate & Pay Invoice"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <InvoicePrintModal
        show={showPrintModal}
        onClose={handlePrintModalClose}
        onPrint={handlePrint}
        invoice={generatedInvoice}
        studentDetails={{
          firstName:
            studentDisplay?.firstName && studentDisplay?.lastName
              ? studentDisplay.firstName
              : studentFromTransport?.name?.split(" ")[0] ?? "",
          lastName:
            studentDisplay?.firstName && studentDisplay?.lastName
              ? studentDisplay.lastName
              : studentFromTransport?.name?.split(" ").slice(1).join(" ") ?? "",
          className: studentFromTransport?.grade ?? "–",
          section: studentFromTransport?.section ?? "–",
          grNumber: studentDisplay?.grNumber ?? studentFromTransport?.grNumber ?? "",
        }}
        printRef={printRef}
        calculateFinalAmount={calculateFinalAmount}
      />
    </>
  )
}

export default TransportCollectFeesModal